import React, { useEffect, useState } from "react";
import { Box, Divider, Dialog } from "@mui/material";
import TableGenerator from "../../../Utils/FormGenerator/TableGenerator";
import FormGenerator from "../../../Utils/FormGenerator/FormGenerator";

import {
  useGetDeclarationQuery,
  useCreateDeclarationMutation,
  useUpdateDeclarationMutation,
  useDeleteDeclarationMutation,
} from "../../../redux/ExportPage/CreateNewJob/Declaration";

import { useUser } from "@/Context/UserContext";
import { SectionHeader } from "../../../Components/Header/SectionsHeader";
import { LuCheck, LuX } from "react-icons/lu";
import { OutLinedButton } from "../../../Components/Buttons/OutLinedButton";
import { BlackButton } from "../../../Components/Buttons/FiledButton";

import declarationSchema from "../../../Config/Export/CreateNewJob/Declaration";
import { BiSolidError } from "react-icons/bi";
import SuccessDialog from "../../../Components/Dialog/SuccessDialog";

export default function Declaration({ onClose }) {
  const { formData, setFormData } = useUser();
  const formRef = React.useRef();

  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [openAddDeclaration, setOpenAddDeclaration] = useState(false);
  const [isRequiredFieldFilled, setIsRequiredFieldFilled] = useState(false);

  const exporterMasterId = formData?.general?.exporterMasterId;
  const declarationId = formData?.declarationId;
  const memoSchema = React.useMemo(() => declarationSchema, []);
  const memoDefaultValues = React.useMemo(
    () => formData.currentDeclarationEdit || {},
    [formData.currentDeclarationEdit]
  );
  const { data, isLoading, refetch } = useGetDeclarationQuery(
    { exporterMasterId },
    { skip: !exporterMasterId }
  );

  const [createDeclaration] = useCreateDeclarationMutation();
  const [updateDeclaration] = useUpdateDeclarationMutation();
  const [deleteDeclaration] = useDeleteDeclarationMutation();

  // ---------------------------------------------------------
  // CREATE DECLARATION
  // ---------------------------------------------------------
  const handleCreateDeclaration = async (formValues) => {
    try {
      const payload = {
        exporterMasterId,
        declarationType: formValues.declarationType,
        declarationCode: formValues.declarationCode,
        declarationInvoice: formValues.declarationInvoice,
        declarationItem: formValues.declarationItem?.id,
        declarationRemarks: formValues.declarationRemarks,
      };

      await createDeclaration(payload).unwrap();

      const tableResponse = await refetch().unwrap();

      setFormData((prev) => ({ ...prev, DeclarationList: tableResponse }));

      setOpenAddDeclaration(false);

      setSuccessDialogConfig({
        label: "Success",
        subtitle: "Declaration created successfully",
        status: "success",
        IconComponent: LuCheck,
        buttonText: "OK",
      });

      setShowSuccessDialog(true);
    } catch (err) {
      setSuccessDialogConfig({
        label: "Failed",
        subtitle: err?.data?.message || "Something went wrong",
        status: "error",
        IconComponent: BiSolidError,
        buttonText: "OK",
      });

      setShowSuccessDialog(true);
    }
  };

  // ---------------------------------------------------------
  // UPDATE DECLARATION
  // ---------------------------------------------------------
  const handleUpdateDeclaration = async (formValues) => {
    const row = formData.currentDeclarationEdit;

    try {
      const payload = {
        exporterMasterId,
        declarationId: row.exportDeclarationId || row.id,
        declarationType: formValues.declarationType,
        declarationCode: formValues.declarationCode,
        declarationInvoice: formValues.declarationInvoice,
        declarationItem: formValues.declarationItem?.id,
        declarationRemarks: formValues.declarationRemarks,
      };

      await updateDeclaration(payload).unwrap();

      const tableResponse = await refetch().unwrap();

      setFormData((prev) => ({
        ...prev,
        DeclarationList: tableResponse,
        currentDeclarationEdit: null,
      }));

      setOpenAddDeclaration(false);

      setSuccessDialogConfig({
        label: "Updated",
        subtitle: "Declaration updated successfully",
        status: "success",
        IconComponent: LuCheck,
        buttonText: "OK",
      });

      setShowSuccessDialog(true);
    } catch (err) {
      console.error(err);

      setSuccessDialogConfig({
        label: "Update Failed",
        subtitle: err?.data?.message || "Something went wrong",
        status: "error",
        IconComponent: BiSolidError,
        buttonText: "OK",
      });

      setShowSuccessDialog(true);
    }
  };

  // ---------------------------------------------------------
  // DELETE DECLARATION
  // ---------------------------------------------------------
  const handleDeleteDeclaration = async (row) => {
    try {
      await deleteDeclaration({
        declarationId: row.declarationId || row.id,
      }).unwrap();

      const tableResponse = await refetch().unwrap();

      setFormData((prev) => ({
        ...prev,
        DeclarationList: tableResponse,
      }));
    } catch (err) {
      console.error("Delete Error", err);
    }
  };

  // ---------------------------------------------------------
  // EDIT CLICK → PREFILL
  // ---------------------------------------------------------
  const handleEditDeclaration = (row) => {
    console.log("🟦 FULL ROW:", row);

    setFormData((prev) => ({
      ...prev,
      currentDeclarationEdit: {
        ...row,
        id: row.exportDeclarationId, // ✔ correct ID
      },
    }));

    setOpenAddDeclaration(true);
  };

  // ---------------------------------------------------------
  // UPDATE GLOBAL TABLE
  // ---------------------------------------------------------
  useEffect(() => {
    if (data) {
      setFormData((prev) => ({
        ...prev,
        DeclarationList: data,
      }));
    }
  }, [data]);

  const visibleFields = [
    { key: "declarationType", label: "Type", width: 120 },
    { key: "declarationCode", label: "Code", width: 120 },
    { key: "declarationInvoice", label: "Invoice", width: 150 },
    { key: "declarationItem", label: "Item", width: 150 },
    { key: "declarationRemarks", label: "Remarks", width: 200 },
  ];

  return (
    <Box>
      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", m: 1 }}>
        <SectionHeader title="Declarations" />
        <LuX
          size={20}
          style={{ cursor: "pointer", border: "none" }}
          onClick={onClose}
        />
      </Box>

      <Divider />

      {/* ADD BUTTON */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
        <OutLinedButton
          label="Add"
          onClick={() => {
            setFormData((prev) => ({ ...prev, currentDeclarationEdit: null }));
            setOpenAddDeclaration(true);
          }}
        />
      </Box>

      {/* TABLE */}
      <TableGenerator
        compKey="DeclarationList"
        visibleFields={visibleFields}
        handlers={{
          handleEdit: handleEditDeclaration,
          handleDelete: handleDeleteDeclaration,
        }}
        loading={isLoading}
        mb={20}
      />

      {/* ADD / EDIT FORM */}
      <Dialog
        open={openAddDeclaration}
        onClose={() => setOpenAddDeclaration(false)}
        fullWidth
        // maxWidth="md"
        PaperProps={{
          sx: {
            p: 0,
            borderRadius: "10px",
            minWidth: "50%",
          },
        }}
      >
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", m: 2 }}>
            <SectionHeader
              title={
                formData.currentDeclarationEdit
                  ? "Edit Declaration"
                  : "Add Declaration"
              }
            />
            <LuX size={22} onClick={() => setOpenAddDeclaration(false)} />
          </Box>

          <Divider />

          <Box sx={{ px: 2 }}>
            <FormGenerator
              compKey="declarationForm"
              ref={formRef}
              schema={memoSchema}
              defaultValues={memoDefaultValues}
              onRequiredFieldsChange={(allFilled) =>
                setIsRequiredFieldFilled(allFilled)
              }
            />
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", p: 2, gap: 2 }}
          >
            <OutLinedButton
              label="Close"
              onClick={() => setOpenAddDeclaration(false)}
            />

            <BlackButton
              label="Save"
              onClick={async () => {
                const formValues = await formRef.current?.submit?.();
                if (!formValues) return;

                if (formData.currentDeclarationEdit) {
                  handleUpdateDeclaration(formValues);
                } else {
                  handleCreateDeclaration(formValues);
                }
              }}
            />
          </Box>
        </Box>
      </Dialog>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        {...successDialogConfig}
      />
    </Box>
  );
}
